import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import type { ReactNode } from 'react';
import { deltaCardProp, formatCompactCurrency, formatPercent } from '../../../lib/format';
import { type BreakdownScope, usePerformanceTab } from '../data/api';
import { UNDER_AVG_TIP, UNDER_SPEND_TIP, UNDER_UPLIFT_TIP } from '../data/constants';
import type { StatCardData } from '../data/mediaBreakdownData';
import type { MediaKpi } from '../data/types';
import { formatKpiValue } from '../data/utils';
import { ErrorState } from '../shared/ErrorState';
import { NoData } from '../shared/NoData';
import { StatCard } from '../shared/StatCard';
import { CreativeTable } from './CreativeTable';
import { FailurePatterns } from './FailurePatterns';

export function UnderPerforming({
  kpi,
  scope,
  enabled,
}: {
  kpi: MediaKpi;
  scope: BreakdownScope;
  enabled: boolean;
}): ReactNode {
  const { data, isLoading, isError } = usePerformanceTab(scope, 'under', enabled);

  if (isError) return <ErrorState />;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (!data || data.creatives.length === 0) {
    return <NoData />;
  }

  const symbol = data.currencySymbol;
  const cards: StatCardData[] = [
    {
      label: 'Spend (under-performing)',
      value: formatCompactCurrency(data.spend, symbol),
      ...deltaCardProp(data.spendDelta),
      deltaInvert: true,
      sub: `${formatPercent(data.spendShare)} of total spend`,
      tone: 'error',
      tip: UNDER_SPEND_TIP,
      tipPlacement: 'right',
    },
    {
      label: 'Potential Uplift',
      value: formatKpiValue(data.potentialUplift, kpi, symbol),
      ...deltaCardProp(data.potentialUpliftDelta),
      tip: UNDER_UPLIFT_TIP,
    },
    {
      label: `Avg. ${kpi.label}`,
      value: formatKpiValue(data.avgKpi, kpi, symbol),
      ...deltaCardProp(data.avgKpiDelta),
      deltaInvert: !kpi.higherIsBetter,
      tip: UNDER_AVG_TIP,
      tipPlacement: 'left',
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: 3,
          mb: 4,
          '@media print': { gridTemplateColumns: 'repeat(3, 1fr)' },
        }}
      >
        {cards.map((card) => (
          <StatCard key={card.label} card={card} />
        ))}
      </Box>

      <FailurePatterns elements={data.elements} />

      <CreativeTable
        title="Lowest media by performance"
        creatives={data.creatives}
        kpi={kpi}
        currencySymbol={symbol}
        accountIds={scope.accountIds ?? []}
      />
    </Box>
  );
}
